/**
 * Como UMA resposta da tela presencial vira o corpo que o backend espera.
 *
 * O `ClinicalAnswerRouter` aceita dois formatos por domínio:
 *   - `value`  — domínios de LISTA (medicamento, sintoma, alergia…): a resposta
 *     é o próprio texto, e cada resposta vira uma linha.
 *   - `data`   — domínios ESTRUTURADOS (sono, álcool, ginecologia…): a resposta
 *     preenche uma coluna específica, quase sempre um ENUM.
 *
 * A REGRA CENTRAL DAQUI: quando a resposta é uma opção, o destino vem pronto na
 * própria definição (`opcao.typed = {column, value}`), calculado na comunicação
 * pelos mesmos mapas que o fluxo do WhatsApp usa.
 *
 * Isso foi aprendido errando, em 10/09: eu mandava o RÓTULO da opção para a
 * coluna. "Ruim - sinto que não descanso o suficiente" foi para
 * `patient_sleep_assessments.quality`, que é `enum('bom','irregular','ruim',
 * 'insonia')` — o banco recusou. E "Tenho diagnóstico de apnéia" nem é
 * qualidade de sono: vai para `has_apnea`, uma coluna diferente. Não existe
 * regra local que acerte isso; existe uma tradução, e ela mora na comunicação.
 */

/**
 * Domínios cujo destino é coluna tipada. Sem `typed` na opção, uma resposta
 * livre aqui NÃO pode ser chutada numa coluna de enum — vai para `notes`.
 */
const DOMINIOS_ESTRUTURADOS = [
  "sleep",
  "gynecology",
  "alcohol",
  "hydration",
  "dietary_recall",
];

/** Domínio estruturado cuja resposta LIVRE tem coluna de texto própria. */
const COLUNA_DE_TEXTO = {
  dietary_recall: "content",
};

/**
 * Domínios que ainda não são preenchíveis por uma resposta única nesta tela.
 *
 * `body_composition` e `physical_activity` SAÍRAM daqui em 10/09: eu tinha
 * bloqueado os dois por raciocínio errado. O backend já agrega peso+altura (e
 * dias+minutos) por `(patient_id, anamnese_id)` e calcula o IMC — as respostas
 * podem chegar em chamadas separadas, exatamente como chegam pelo WhatsApp. O
 * que faltava não era agregação: era saber QUAL coluna cada pergunta preenche,
 * e isso agora vem na definição (`pergunta.typed`).
 *
 * Ficou VAZIO. Se algum domínio voltar a precisar de bloqueio, ele aparece na
 * tela com o motivo escrito, em vez de sumir — pergunta que desaparece sem
 * explicação é pior que pergunta que diz por que não dá para responder.
 *
 * Nota sobre `exercise`: ele não é bloqueado, mas hoje roteia para
 * `clinical_note` (interino, marcado assim no próprio `FIELD_KEY_BY_STATE`),
 * então a resposta vira nota clínica e não linha em `exercise_practices`.
 */
export const DOMINIOS_MULTI_CAMPO = [];

/**
 * Monta o corpo da resposta para o backend.
 *
 * @param {string} fieldKey domínio de destino
 * @param {string|number} resposta o que o médico registrou (rótulo ou texto)
 * @param {object} [opcao] a opção escolhida, quando a resposta veio de uma lista
 * @param {object} [pergunta] a pergunta, que pode declarar a coluna de destino
 * @returns {{value: any}|{data: object}}
 */
export function corpoDaResposta(fieldKey, resposta, opcao, pergunta) {
  // 1. A definição já diz para onde vai. É o caminho certo, e o único que acerta
  //    coluna diferente da esperada (apnéia -> has_apnea, e não quality).
  if (opcao && opcao.typed && opcao.typed.column) {
    return { data: { [opcao.typed.column]: opcao.typed.value } };
  }

  // 2. Coluna tipada da PERGUNTA (altura -> height_cm, dias -> days_per_week).
  //    A tela pede o número já na unidade certa, então não há texto para
  //    interpretar. Faixa e conversão são conferidas no router do Laravel, que
  //    protege TODAS as portas — não só esta.
  if (pergunta && pergunta.typed && pergunta.typed.column) {
    const numero = Number(String(resposta).replace(",", "."));
    if (Number.isFinite(numero)) {
      return { data: { [pergunta.typed.column]: numero } };
    }
    return { data: { notes: String(resposta) } };
  }

  if (DOMINIOS_ESTRUTURADOS.includes(fieldKey)) {
    const colunaTexto = COLUNA_DE_TEXTO[fieldKey];
    if (colunaTexto) {
      return { data: { [colunaTexto]: resposta } };
    }

    // Hidratação guarda litros num decimal: só vai para a coluna se for número.
    if (fieldKey === "hydration") {
      const litros = Number(String(resposta).replace(",", "."));
      if (Number.isFinite(litros)) {
        return { data: { liters_per_day: litros } };
      }
    }

    // Sem destino tipado, o texto vira observação. Perde estrutura, não perde
    // o dado — e não derruba o registro com um enum inválido.
    return { data: { notes: String(resposta) } };
  }

  return { value: resposta };
}

/** Uma pergunta é respondível nesta tela? */
export function podeResponder(fieldKey) {
  return Boolean(fieldKey) && !DOMINIOS_MULTI_CAMPO.includes(fieldKey);
}

/** Por que não dá para responder — texto mostrado na própria conversa. */
export function motivoBloqueio(fieldKey) {
  if (!fieldKey) {
    return "Esta pergunta ainda não tem destino definido no prontuário. Salve o template de novo para que o destino seja detectado.";
  }

  if (DOMINIOS_MULTI_CAMPO.includes(fieldKey)) {
    return "Esta pergunta precisa de mais de um dado (ex.: peso e altura) e ainda não é preenchível por aqui. Registre na aba do prontuário.";
  }

  return "";
}


/**
 * O que de fato vai para o backend a partir de UMA resposta.
 *
 * Nem toda resposta é dado clínico, e essa distinção não é inventada aqui: ela
 * vem da definição, com as mesmas regras que o fluxo do WhatsApp usa.
 *
 *  - **Gate de negação** (`pergunta.denialGate`): "usa medicamento contínuo?"
 *    respondido com "Não" emite `denies_continuous_medication`, que faz o router
 *    parar registros contraditórios. Respondido com "Sim" **não emite nada** — o
 *    detalhe vem na pergunta seguinte. Sem essa regra a tela gravou, em test, um
 *    medicamento chamado "Sim" e outro chamado "Não".
 *  - **Opção de navegação** (`opcao.navigation`): "Nenhum Sintoma" e "Outro(s)"
 *    escolhem o caminho; o sintoma real vem depois. Gravar o rótulo criou um
 *    sintoma chamado "Outro(s)".
 *
 * @returns {Array<{fieldKey: string, corpo: object}>} pode ser vazio
 */
export function paraEnviar(pergunta, respostas) {
  const gate = pergunta?.denialGate;

  if (gate) {
    const negou = respostas.some(
      (r) => normalizar(r) === "nao" || normalizar(r) === "no",
    );

    // A chave já carrega a negação; o valor só precisa ser verdadeiro.
    return negou ? [{ fieldKey: gate, corpo: { value: "true" } }] : [];
  }

  const opcoes = pergunta?.options || [];

  return respostas
    .map((r) => ({ resposta: r, opcao: opcoes.find((op) => op.label === r) }))
    .filter(({ opcao }) => !(opcao && opcao.navigation))
    .map(({ resposta, opcao }) => ({
      fieldKey: pergunta.fieldKey,
      corpo: corpoDaResposta(pergunta.fieldKey, resposta, opcao, pergunta),
    }));
}

function normalizar(texto) {
  return String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}
